import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, Drawer, AppBar, Toolbar, List, Typography, Divider, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar, Menu, MenuItem, Tooltip } from '@mui/material';
import { 
  Dashboard, 
  DirectionsBike, 
  Build,
  Upload, 
  Compare, 
  Inventory2,
  CleaningServices,
  MenuBook,
  AutoAwesome, 
  Person, 
  Menu as MenuIcon, 
  Logout, 
  Language, 
  DarkMode, 
  LightMode 
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useThemeMode } from '../context/ThemeContext';
import ScrollToTop from './ScrollToTop';

const drawerWidth = 280;

const menuGroups = [
  {
    label: '工作流',
    items: [
      { text: 'dashboard', icon: <Dashboard />, path: '/dashboard' },
      { text: 'configs', icon: <DirectionsBike />, path: '/configs' },
      { text: 'configBuilder', icon: <Build />, path: '/builder' },
      { text: 'uploadConfig', icon: <Upload />, path: '/upload' },
      { text: 'generate', icon: <AutoAwesome />, path: '/generate' }
    ]
  },
  {
    label: '数据与规范',
    items: [
      { text: 'componentLibrary', icon: <Inventory2 />, path: '/components' },
      { label: '数据治理', icon: <CleaningServices />, path: '/governance' },
      { text: 'standardsGuide', icon: <MenuBook />, path: '/standards' },
      { text: 'compare', icon: <Compare />, path: '/compare' }
    ]
  }
];
const menuItems = menuGroups.flatMap((group) => group.items);

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);
  const activeMenuItem = menuItems.find((item) => (
    location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  ));
  const activeMenuLabel = activeMenuItem?.label || (activeMenuItem?.text ? t(activeMenuItem.text) : t('appName'));
  const isDetailRoute = Boolean(activeMenuItem && location.pathname !== activeMenuItem.path);
  const getItemLabel = (item) => item.label || t(item.text);
  const isItemActive = (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const heading = mainRef.current?.querySelector('h1, h2, [role="heading"]');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ 
        background: mode === 'dark'
          ? 'linear-gradient(180deg, #091427 0%, #10213a 100%)'
          : 'linear-gradient(180deg, #16325b 0%, #21456f 100%)',
        color: '#fff',
        minHeight: 96,
        alignItems: 'center',
        px: 2.25,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at top right, rgba(34,197,94,0.18), transparent 28%)',
          pointerEvents: 'none'
        }
      }}>
        <Avatar
          sx={{
            mr: 1.5,
            width: 42,
            height: 42,
            bgcolor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            boxShadow: '0 14px 28px rgba(5, 12, 23, 0.22)',
            flexShrink: 0
          }}
        >
          <DirectionsBike sx={{ fontSize: 24 }} />
        </Avatar>
        <Box sx={{ minWidth: 0, position: 'relative', zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{ color: 'rgba(255,255,255,0.76)', lineHeight: 1.1 }}
          >
            配置管理工作台
          </Typography>
          <Typography
            variant="h6"
            fontWeight="bold"
            title={t('appName')}
            sx={{
              fontSize: '1.08rem',
              lineHeight: 1.2,
              letterSpacing: '-0.015em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
              wordBreak: language === 'zh' ? 'break-all' : 'break-word'
            }}
          >
            {t('appName')}
          </Typography>
        </Box>
      </Toolbar>
      
      <Divider />
      
      <List sx={{ flex: 1, px: 1, py: 1.5 }}>
        {menuGroups.map((group) => (
          <Box key={group.label} sx={{ mb: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', px: 1.5, mb: 0.5 }}>
              {group.label}
            </Typography>
            {group.items.map((item) => (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={isItemActive(item)}
                  aria-current={isItemActive(item) ? 'page' : undefined}
                  onClick={() => { navigate(item.path); if(window.innerWidth<900) setMobileOpen(false); }}
                  sx={{
                    borderRadius: 2,
                    minHeight: 44,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: '#fff',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '& .MuiListItemIcon-root': { color: '#fff' }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={getItemLabel(item)}
                    primaryTypographyProps={{ fontWeight: isItemActive(item) ? 600 : 400 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </Box>
        ))}
      </List>
      
      <Divider />
      <List sx={{ px: 1 }}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton 
            onClick={() => navigate('/profile')}
            sx={{ borderRadius: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}><Person /></ListItemIcon>
            <ListItemText primary={t('profile')} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'fixed',
          top: 8,
          left: 8,
          zIndex: 2000,
          transform: 'translateY(-160%)',
          bgcolor: 'background.paper',
          color: 'text.primary',
          px: 2,
          py: 1,
          borderRadius: 2,
          boxShadow: 4,
          '&:focus': { transform: 'translateY(0)' }
        }}
      >
        跳到主要内容
      </Box>
      <AppBar 
        position="fixed" 
        sx={{ 
          width: { md: `calc(100% - ${drawerWidth}px)` }, 
          ml: { md: `${drawerWidth}px` },
          bgcolor: mode === 'dark' ? '#1e1e1e' : '#fff',
          color: mode === 'dark' ? '#fff' : 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="打开导航菜单"
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
              {activeMenuLabel}
            </Typography>
            {isDetailRoute && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {activeMenuLabel} / 详情
              </Typography>
            )}
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mr: 2, display: { xs: 'none', lg: 'block' } }}
          >
            聚焦配置、零件、导入、标准与对比
          </Typography>
          
          <Tooltip title={mode === 'dark' ? '切换到浅色模式' : '切换到深色模式'}>
            <IconButton aria-label={mode === 'dark' ? '切换到浅色模式' : '切换到深色模式'} color="inherit" onClick={toggleTheme} sx={{ mr: 1 }}>
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>
          <Tooltip title="切换语言">
            <IconButton aria-label="切换语言" color="inherit" onClick={toggleLanguage}>
              <Language />
            </IconButton>
          </Tooltip>
          <Tooltip title="打开用户菜单">
            <IconButton aria-label="打开用户菜单" color="inherit" onClick={handleMenu}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu 
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              {t('profile')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
              {t('logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: 'none',
              bgcolor: mode === 'dark' ? '#1e1e1e' : '#fff',
              boxShadow: '1px 0 3px rgba(0,0,0,0.1)'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box 
        component="main" 
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        sx={{ 
          flexGrow: 1, 
          minWidth: 0,
          maxWidth: '100%',
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` }, 
          mt: 8,
          minHeight: 'calc(100dvh - 64px)'
        }}
      >
        <Outlet />
        <ScrollToTop />
      </Box>
    </Box>
  );
}
